import {
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    Image,
    View
} from "react-native";
import type CampaignDetailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Sticker,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    CampaignRequestDetailPayloadType
} from "../../../../services/restAPI/actions/getCommercialOpportunityRequest/type";
import {
    Building2Icon,
    CalendarIcon,
    CheckIcon,
    UserIcon,
    XIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";

const CampaignDetailModal = ({
    historyItem = null,
    isHistory = false,
    isVisible,
    onApprove,
    requestID,
    onReject,
    onClose
}: CampaignDetailModalProps) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        detail,
        setDetail
    ] = useState<CampaignRequestDetailPayloadType | null>(null);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    useEffect(() => {
        if (!isVisible) {
            setDetail(null);
            return;
        }

        if (historyItem) {
            setDetail({
                coverPhotoPath: historyItem.coverPhotoPath,
                companyTitle: historyItem.companyTitle,
                discountRate: historyItem.discountRate,
                description: historyItem.description,
                endDate: historyItem.endDate || "",
                companyID: historyItem.companyID,
                createdAt: historyItem.createdAt,
                requestID: historyItem.historyID,
                userName: historyItem.userName,
                companyLogoPath: undefined,
                userID: historyItem.userID,
                title: historyItem.title,
                userPhone: undefined,
                userMail: undefined
            });
            setLoading(false);
            return;
        }

        if (!requestID) {
            setDetail(null);
            return;
        }

        const fetchDetail = async () => {
            try {
                setLoading(true);
                const response = await RESTServiceContext.action("getCommercialOpportunityRequest", {
                    requestID
                });

                if (response.payload) {
                    setDetail(response.payload);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [
        historyItem,
        requestID,
        isVisible
    ]);

    const isExpired = detail?.endDate ? new Date(detail.endDate) <= new Date() : false;

    const renderContent = () => {
        if (loading) {
            return <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        if (!detail) {
            return <View
                style={[
                    stylesheet.errorContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.low}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-detail-load-error")}
                </Text>
            </View>;
        }

        return <ScrollView
            style={stylesheet.contentContainer}
            showsVerticalScrollIndicator={true}
        >
            <View
                style={[
                    stylesheet.infoContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                {
                    detail.coverPhotoPath ?
                        <Image
                            style={[
                                stylesheet.coverImage,
                                {
                                    borderRadius: radiuses.md
                                }
                            ]}
                            source={{
                                uri: detail.coverPhotoPath
                            }}
                            resizeMode="cover"
                        />
                    :
                        null
                }
                <View
                    style={[
                        stylesheet.infoRow,
                        {
                            justifyContent: "space-between"
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.infoRow,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Sticker
                            title={`%${detail.discountRate} ${localize("requests-field-discount")}`}
                            spreadBehaviour="free"
                            type="success"
                            size="small"
                        />
                        {
                            isExpired ?
                                <Sticker
                                    title={localize("requests-campaign-expired")}
                                    spreadBehaviour="free"
                                    type="danger"
                                    size="small"
                                />
                            :
                                null
                        }
                    </View>
                    <View
                        style={[
                            stylesheet.infoRow,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <CalendarIcon
                            color={isExpired ? colors.system.default.error : colors.content.icon.mid}
                            size={16}
                        />
                        <Text
                            customColor={isExpired ? colors.system.default.error : colors.content.text.mid}
                            variant="bodySmallSize"
                        >
                            {moment(detail.endDate).format("DD.MM.YYYY")}
                        </Text>
                    </View>
                </View>
                <Text
                    customColor={colors.content.text.high}
                    variant="titleLargeSize"
                >
                    {detail.title}
                </Text>
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {detail.description}
                </Text>
                <View
                    style={[
                        stylesheet.infoRow,
                        {
                            backgroundColor: colors.content.container.subtle,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.md,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    {
                        detail.companyLogoPath ?
                            <Image
                                style={[
                                    stylesheet.companyLogo,
                                    {
                                        borderRadius: radiuses.sm
                                    }
                                ]}
                                source={{
                                    uri: detail.companyLogoPath
                                }}
                            />
                        :
                            <Building2Icon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                    }
                    <View>
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodyMediumSize"
                        >
                            {detail.companyTitle || "-"}
                        </Text>
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelSmallSize"
                        >
                            {localize("requests-field-company")}
                        </Text>
                    </View>
                </View>
                {
                    detail.userName ?
                        <View
                            style={[
                                stylesheet.infoRow,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <UserIcon
                                color={colors.content.icon.mid}
                                size={18}
                            />
                            <Text
                                customColor={colors.content.text.mid}
                                variant="bodySmallSize"
                            >
                                {localize("requests-submitted-by")}: {detail.userName} {detail.userPhone ? `(${detail.userPhone})` : ""}
                            </Text>
                        </View>
                    :
                        null
                }
            </View>
        </ScrollView>;
    };

    return <Modal
        id="campaign-detail-modal"
        portalName="modal-system"
        isActive={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.headerContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleMediumSize"
                >
                    {localize("requests-detail-campaign-title")}
                </Text>
                <TouchableOpacity
                    style={stylesheet.closeButton}
                    onPress={onClose}
                >
                    <XIcon
                        color={colors.content.icon.mid}
                        size={24}
                    />
                </TouchableOpacity>
            </View>
            {renderContent()}
            <View
                style={[
                    stylesheet.footerContainer,
                    {
                        justifyContent: !isHistory && (onReject || onApprove) ? "space-between" : "flex-end"
                    }
                ]}
            >
                <Button
                    title={localize("notifications-detail-close")}
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    onPress={onClose}
                    variant="filled"
                />
                {
                    !isHistory && (onReject || onApprove) ?
                        <View
                            style={[
                                stylesheet.footerActionsContainer,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {
                                onReject ?
                                    <Button
                                        onPress={() => requestID && onReject(requestID)}
                                        title={localize("requests-action-reject")}
                                        icon={({
                                            color,
                                            size
                                        }) => <XIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        spreadBehaviour="free"
                                        variant="filled"
                                        type="danger"
                                    />
                                :
                                    null
                            }
                            {
                                onApprove ?
                                    <Button
                                        onPress={() => {
                                            if(isExpired) {
                                                NCoreUIKitSnackBar.open({
                                                    title: localize("requests-campaign-expired-warning")
                                                });
                                                return;
                                            }
                                            if(requestID) {
                                                onApprove(requestID);
                                            }
                                        }}
                                        title={localize("requests-action-approve")}
                                        icon={({
                                            color,
                                            size
                                        }) => <CheckIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        spreadBehaviour="free"
                                        variant="filled"
                                        type="success"
                                    />
                                :
                                    null
                            }
                        </View>
                    :
                        null
                }
            </View>
        </View>
    </Modal>;
};
export default CampaignDetailModal;
